import { useState, useCallback, useEffect } from 'react';
import { pb, extractMfaId, pbErrorMessage, USERS_COLLECTION } from '../services/pocketbase';
import { syncMfaState } from '../services/api';

interface Props {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  /** Email destinataire du code. Optionnel : on retombe sur l'enregistrement auth courant. */
  email?: string;
}

type Step = 'intro' | 'verify' | 'success';

// ══════════════════════════════════════════════════════════════════════════
//  Activation du second facteur — code à usage unique par EMAIL
// ══════════════════════════════════════════════════════════════════════════
//
// ⚠️ CHANGEMENT DE MÉCANISME PAR RAPPORT À SUPABASE
// -------------------------------------------------
// L'ancien écran enrôlait un facteur TOTP (RFC 6238) : QR code, secret partagé,
// application d'authentification. PocketBase n'implémente PAS le TOTP. Son MFA
// consiste à exiger DEUX méthodes d'authentification différentes parmi celles
// qu'il connaît (mot de passe, OTP email, OAuth2) — en pratique ici :
// mot de passe, puis code à usage unique envoyé par email.
//
// Conséquence directe sur cet écran : il n'y a plus de « facteur » à créer,
// donc plus de QR code ni de secret. Ce qui reste à faire, c'est :
//   1. prouver que l'utilisateur reçoit bien les codes sur son email —
//      sinon activer le second facteur revient à se verrouiller dehors ;
//   2. lever le drapeau `users.mfa_enrolled` via `POST /api/auth/mfa-sync`,
//      drapeau sur lequel s'appuie la règle MFA de la collection PocketBase.
//
// Le test du point 1 réauthentifie l'utilisateur par OTP : le jeton de
// l'authStore est remplacé par un jeton équivalent pour le même compte.

export default function MfaEnrollModal({ visible, onClose, onSuccess, email }: Props) {
  const [step, setStep] = useState<Step>('intro');
  const [otpId, setOtpId] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const targetEmail = email ?? (pb.authStore.record?.email as string | undefined) ?? '';

  // Réinitialise l'écran à chaque ouverture.
  useEffect(() => {
    if (!visible) return;
    setStep('intro');
    setOtpId(null);
    setCode('');
    setError(null);
    setLoading(false);
  }, [visible]);

  const handleSendCode = useCallback(async () => {
    if (!targetEmail) {
      setError("Adresse email introuvable sur votre compte. Contactez le support.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await pb.collection(USERS_COLLECTION).requestOTP(targetEmail);
      setOtpId(res.otpId);
      setStep('verify');
    } catch (e) {
      setError(pbErrorMessage(e, "Impossible d'envoyer le code. Réessayez dans un instant."));
    } finally {
      setLoading(false);
    }
  }, [targetEmail]);

  const handleVerify = useCallback(async () => {
    if (!otpId) return;
    const normalized = code.replace(/\s+/g, '');
    if (normalized.length < 4) {
      setError('Saisissez le code reçu par email.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Le second facteur n'est pas encore actif pour ce compte, donc cette
      // authentification par OTP seule doit aboutir directement.
      await pb.collection(USERS_COLLECTION).authWithOTP(otpId, normalized);
      await syncMfaState(true);
      setStep('success');
    } catch (e) {
      if (extractMfaId(e)) {
        // Le serveur exige déjà deux facteurs pour ce compte : on ne peut pas
        // valider l'email avec l'OTP seul, et l'activation est de toute façon
        // déjà effective côté PocketBase.
        setError("La double authentification est déjà imposée par la configuration du serveur pour ce compte.");
      } else {
        setError(pbErrorMessage(e, 'Code invalide ou expiré.'));
      }
      setCode('');
    } finally {
      setLoading(false);
    }
  }, [otpId, code]);

  const handleCancel = useCallback(() => {
    // Rien à annuler côté serveur : aucun facteur n'a été créé tant que
    // `mfa-sync` n'a pas été appelé.
    setStep('intro');
    setOtpId(null);
    setCode('');
    setError(null);
    onClose();
  }, [onClose]);

  const handleFinish = useCallback(() => {
    setStep('intro');
    setOtpId(null);
    setCode('');
    setError(null);
    onSuccess();
  }, [onSuccess]);

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-cream-app rounded-3xl border-2 border-ink max-w-md w-full p-6 space-y-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-slate-900">
              {step === 'success' ? 'Double authentification activée' : 'Activer la double authentification'}
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              {step === 'success'
                ? 'Votre compte est désormais protégé par un second facteur.'
                : 'Un code à usage unique vous sera envoyé par email à chaque connexion.'}
            </p>
          </div>
          <button
            onClick={handleCancel}
            disabled={loading}
            className="text-slate-400 hover:text-slate-600 text-xl leading-none shrink-0"
          >
            &times;
          </button>
        </div>

        {/* ── Étape : explication + envoi du code de vérification ── */}
        {step === 'intro' && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600 leading-relaxed">
              Une fois activée, chaque connexion demandera votre mot de passe
              <strong> puis </strong> un code à usage unique envoyé à&nbsp;:
            </p>
            <p className="text-sm font-medium text-slate-900 bg-slate-100 rounded-lg px-3 py-2 break-all">
              {targetEmail || 'adresse inconnue'}
            </p>
            <p className="text-xs text-slate-500 leading-relaxed">
              Nous envoyons d'abord un code de vérification pour confirmer que vous
              recevez bien nos emails. Sans cette confirmation, activer la double
              authentification risquerait de vous verrouiller hors de votre compte.
            </p>

            {error && <p className="text-sm text-red-500">{error}</p>}

            <button
              onClick={handleSendCode}
              disabled={loading || !targetEmail}
              className="btn-ink w-full py-2.5 text-sm"
            >
              {loading ? 'Envoi...' : 'Envoyer le code de vérification'}
            </button>
          </div>
        )}

        {/* ── Étape : saisie du code ── */}
        {step === 'verify' && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Entrez le code que vous venez de recevoir à l'adresse{' '}
              <strong className="break-all">{targetEmail}</strong>.
            </p>

            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={16}
              value={code}
              onChange={e => setCode(e.target.value.replace(/\s+/g, ''))}
              onKeyDown={e => {
                if (e.key === 'Enter' && code.length >= 4) handleVerify();
              }}
              autoFocus
              placeholder="Code reçu par email"
              disabled={loading}
              className="w-full px-4 py-3 text-center text-xl tracking-[0.3em] font-mono rounded-lg border border-slate-300 bg-cream-app text-slate-900 placeholder:text-sm placeholder:tracking-normal placeholder:font-sans focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-shadow"
            />

            {error && <p className="text-sm text-red-500">{error}</p>}

            <div className="flex items-center justify-between gap-2">
              <button
                onClick={handleSendCode}
                disabled={loading}
                className="text-xs text-violet-600 hover:underline disabled:opacity-50"
              >
                Renvoyer un code
              </button>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { setStep('intro'); setError(null); setCode(''); }}
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium rounded-lg text-slate-700 hover:bg-slate-100 transition-colors disabled:opacity-50"
                >
                  Retour
                </button>
                <button
                  onClick={handleVerify}
                  disabled={loading || code.length < 4}
                  className="btn-ink px-4 py-2 text-sm"
                >
                  {loading ? 'Vérification...' : 'Valider'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Étape : succès ── */}
        {step === 'success' && (
          <div className="space-y-4">
            <div className="flex items-center justify-center py-4">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
                <svg className="w-8 h-8 text-emerald-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
            </div>
            <p className="text-sm text-slate-600 text-center">
              Lors de votre prochaine connexion, un code à usage unique vous sera envoyé
              par email en plus de votre mot de passe. Gardez l'accès à votre boîte
              email&nbsp;: c'est elle qui protège désormais votre compte.
            </p>
            <button
              onClick={handleFinish}
              className="btn-ink w-full py-2.5 text-sm"
            >
              Terminé
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
